import { uniq } from 'lodash'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { MotorTradeBuySteps } from '@/enums/motorTradeBuySteps'

export interface OptionItem {
  value: number | string
  text: string
  relationshipOptions?: string[]
  abiGroup?: number
  maxGVW?: number
  requireShunterRadius?: boolean
  requireCarJockeyRadius?: boolean
  isComprehensive?: boolean
  isSocialDomesticPleasure?: boolean
  requiresQuadBikes?: boolean
  requiresRallyTrackKitCarsTrikes?: boolean
  excesses?: any
  category?: string
  customerLOI?: boolean
  requireNCBIntroRenewal?: boolean
  maxCC?: number | undefined
  countries?: string
  ageFrom?: null | number
  ageTo?: null | number
  additionalExcess?: string
  indemnities?: { value: number | string; text: string }[]
  cCs?: { value: number | string; text: string }[]
  defaultAdditionalIndemnity?: string
  defaultCc?: string
  numericValue?: number
  gvwLimitDecimal?: number | null
}

interface IBrokerDetail {
  broker?: {
    brokerName: string
    brokerId: string
  }
  setBroker: (broker?: any) => void
  brokerReset: () => void
}

interface IClient {
  clientId?: string
  setClient: (clientId?: string) => void
  clientReset: () => void
}

interface IBackoffice {
  backoffice?: boolean
  setBackoffice: (backoffice?: boolean) => void
  backofficeReset: () => void
}
interface IFormStore {
  step: number
  stepAsEnum: MotorTradeBuySteps
  stepPath: MotorTradeBuySteps[]
  formData: any
  setData: (state: any) => void
  setStep: (step: MotorTradeBuySteps) => void
  reset: () => void
}

interface IMotorTradeGuid {
  guid?: string
  setGuid: (guid?: string) => void
  guidReset: () => void
}

interface ITradePlates {
  existingTradePlates: boolean
  setExistingTradePlates: (existingTradePlates?: boolean) => void
  tradeReset: () => void
}

interface IAbiValidator {
  abiVariables?: {
    driverMaxAbi: any
    driverPlanMaxAbi: any
  }
  setDriverMaxAbi: (abiVariables?: any) => void
  abiReset: () => void
}

interface IGvwValidator {
  gvwVariables?: {
    driverListGvw: any
    driverPlanGvw: any
  }
  setDriverGvw: (gvwVariables?: any) => void
  gvwReset: () => void
}

export interface IOptions {
  setOptions: (options: any) => void
  options?: {
    proposerTitles: OptionItem[]
    coverLevels: OptionItem[]
    youngDriverConfiguration: OptionItem[]
    aadDriverMaxAge: OptionItem[]
    aadDriverMinAge: OptionItem[]
    europeanTripCover: OptionItem[]
    europeanTripUsage: OptionItem[]
    europeanArea: OptionItem[]
    aadMaxVehicleGrouping: OptionItem[]
    aadMaxVehicleGvw: OptionItem[]
    aadMaxMotorcycleCc: OptionItem[]
    demonstrationCovers: OptionItem[]
    companyTypes: OptionItem[]
    carJockeyRadii: OptionItem[]
    marketingConsents: OptionItem[]
    marketingMethods: OptionItem[]
    driverClaimStatuses: OptionItem[]
    driverClaimTypes: OptionItem[]
    driverCCJStatuses: OptionItem[]
    driverCriminalConvictionCodes: OptionItem[]
    driverGVWLimits: OptionItem[]
    driverLicenceTypes: OptionItem[]
    driverMaxABIGroups: OptionItem[]
    noClaimBonusIntroRenewalYears: OptionItem[]
    driverMotorcycleCovers: OptionItem[]
    driverMTConvictionCodes: OptionItem[]
    driverPlans: OptionItem[]
    driverRelationshipsPolicyHolder: OptionItem[]
    driverResidencyLenghts: OptionItem[]
    driverTitles: OptionItem[]
    driverTradeEmploymentBasises: OptionItem[]
    driverExperienceBasedExcesses: OptionItem[]
    driverExperienceBasedVehicleCCLimits: OptionItem[]
    driverUsages: OptionItem[]
    excesses: OptionItem[]
    youngDriverIndemnityLimit: OptionItem[]
    excessLates: OptionItem[]
    indemnityCustomerVehicles: OptionItem[]
    indemnityOwnVehicles: OptionItem[]
    mtOccupations: OptionItem[]
    noClaimBonuses: OptionItem[]
    noClaimBonusesEarned: OptionItem[]
    noClaimBonusPreviousInsurers: OptionItem[]
    occupations: OptionItem[]
    thirdPartyDamageLimits: OptionItem[]
    tradingFroms: OptionItem[]
    tradingPremiseTypes: OptionItem[]
    customerLoanCoverLevels: OptionItem[]
    vehicleKeptOvernightType: OptionItem[]
    vehicleOwnerTypes: OptionItem[]
    vehicleModifications: OptionItem[]
    vehicleStatuses: OptionItem[]
    windScreenCovers: OptionItem[]
    vehicleType: OptionItem[]
  }
}
const substrStepEnum = (step: MotorTradeBuySteps): number =>
  Number(step.substring(4))

export const motorTradeFormStore = create<IFormStore>()(
  persist(
    (set, get) => ({
      stepAsEnum: MotorTradeBuySteps.Step1,
      step: substrStepEnum(get()?.stepAsEnum || MotorTradeBuySteps.Step1),
      stepPath: [MotorTradeBuySteps.Step1],
      formData: {},

      setData: (newData: any) =>
        set((state) => ({
          formData: { ...state.formData, ...newData },
        })),
      setStep: (step: MotorTradeBuySteps) =>
        set((state) => ({
          step: substrStepEnum(step),
          stepAsEnum: step,
          stepPath: uniq([...state.stepPath, step]),
        })),
      reset: () => {
        set(() => ({
          formData: {},
          stepAsEnum: MotorTradeBuySteps.Step1,
          step: substrStepEnum(MotorTradeBuySteps.Step1),
        }))
      },
    }),
    {
      name: 'motorTradeFormStore',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const gvwValidatorStore = create<IGvwValidator>()(
  persist(
    (set) => ({
      gvwVariables: undefined,
      setDriverGvw(gvwVariables) {
        set(() => ({
          gvwVariables: {
            driverListGvw: gvwVariables?.driverListGvw,
            driverPlanGvw: gvwVariables?.driverPlanGvw,
          },
        }))
      },
      gvwReset: () => set(() => ({ gvwVariables: undefined })),
    }),
    {
      name: 'gvwDetail',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const abiValidatorStore = create<IAbiValidator>()(
  persist(
    (set) => ({
      abiVariables: undefined,
      setDriverMaxAbi(abiVariables) {
        set(() => ({
          abiVariables: {
            driverMaxAbi: abiVariables?.driverMaxAbi,
            driverPlanMaxAbi: abiVariables?.driverPlanMaxAbi,
          },
        }))
      },
      abiReset: () => set(() => ({ abiVariables: undefined })),
    }),
    {
      name: 'abiDetail',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const mtBrokerDetailStore = create<IBrokerDetail>()(
  persist(
    (set) => ({
      broker: undefined,
      setBroker(broker) {
        set(() => ({
          broker: {
            brokerId: broker.brokerId,
            brokerName: broker.brokerName,
          },
        }))
      },
      brokerReset: () => set(() => ({ broker: undefined })),
    }),
    {
      name: 'brokerDetail',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const isMtBackofficeStore = create<IBackoffice>()(
  persist(
    (set) => ({
      backoffice: false,
      setBackoffice(backoffice) {
        set(() => ({ backoffice: backoffice }))
      },
      backofficeReset: () => set(() => ({ backoffice: false })),
    }),
    {
      name: 'isBackoffice',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const mtClientStore = create<IClient>()(
  persist(
    (set) => ({
      clientId: undefined,
      setClient(clientId) {
        set(() => ({ clientId: clientId }))
      },
      clientReset: () => set(() => ({ clientId: undefined })),
    }),
    {
      name: 'clientId',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const motorTradeOptionsFormStore = create<IOptions>()((set) => ({
  options: undefined,
  /**
   * Merges new options with the existing options in the store state.
   * @param {any} options The new options to merge.
   * @returns {void}
   */
  setOptions: (options: any) =>
    set((state) => ({ options: { ...state.options, ...options } })),
}))

export const motorTradeGuidStore = create<IMotorTradeGuid>()(
  persist(
    (set) => ({
      guid: undefined,
      setGuid(guid) {
        set(() => ({ guid: guid }))
      },
      guidReset: () => set(() => ({ guid: undefined })),
    }),
    {
      name: 'brokerGuid',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const existingTradePlatesStore = create<ITradePlates>()(
  persist(
    (set) => ({
      existingTradePlates: false,
      setExistingTradePlates(trade) {
        set(() => ({ existingTradePlates: trade }))
      },
      tradeReset: () => set(() => ({ existingTradePlates: undefined })),
    }),
    {
      name: 'existingTradePlates',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)
